import { notificationsActions } from 'redux/notifications/notificationsSlice'
import DropdownV2 from 'core-system/Dropdown/DropdownV2'
import { SelectItemProps } from 'core-system/Dropdown'
import LinkIcon from 'core-system/Icons/Actions/Link'
import OverflowIcon from 'core-system/Icons/Misc/Overflow'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import ToggleWorksiteInviteCodeModal from 'features/Accounts/Users/ToggleWorksiteInviteCodeModal'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { Worksite } from 'redux/employer/employerTypes'
import RandomUtils from 'shared/RandomUtils'
import styled from 'styled-components'
import ArchiveWorksiteModal from './ArchiveWorksiteModal'
import UnarchiveWorksiteModal from './UnarchiveWorksiteModal'
import EditWorksiteModal from './EditWorksiteModal'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import moment from 'moment'
import EmployeesIcon from 'core-system/Icons/Misc/Employees'
import LocationIcon from 'core-system/Icons/Misc/Location'
import FlexContainer from 'core-system/FlexContainer'
import AddWorksiteModal from './AddWorksiteModal'
import PageHeader from '../components/PageHeader'
import PageBodyContainer from '../components/PageBodyContainer'
import ColorPill from 'core-system/Pill/ColorPill'
import PillContainer from 'core-system/Pill/Pill'
import { getAllProgramForColumn } from '../helperFunctions'
import InviteUsersModal from '../components/InviteUsersModal'
import SegmentInfoDrawer from './SegmentInfoDrawer'
import SegmentsDisplay from 'features/Programs/Shared/components/SegmentsDisplay'
import wordings from 'features/Programs/Shared/wordings.json'
import SegmentOperations from './SegmentOperations'

const WorksiteInfo = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const TopContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
`

const Alias = styled.div`
  ${(props) => props.theme.typography.action4}
  color: ${(props) => props.theme.palette.text.primary};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const WorksiteAddress = styled.div`
  ${(props) => props.theme.typography.body2}
  color: ${(props) => props.theme.palette.text.placeholder};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const InviteContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 0.5rem;
  padding: 0 0.4375rem 0 0.25rem;

  &:hover {
    background-color: ${(props) => props.theme.palette.secondary.purple300};
  }
`

const IconContainer = styled.div`
  min-width: 1.5rem;
  display: flex;
  align-items: center;
`

const handleLinkClick = (
  e: React.MouseEvent<HTMLDivElement>,
  dispatch: any,
  worksite: Worksite,
  setSelectedWorksite: (worksite: Worksite) => void,
  setIsInviteModalOpen: (open: boolean) => void
) => {
  e.stopPropagation()
  if (worksite.inviteCode) {
    RandomUtils.copyInviteCode(worksite.address, worksite.inviteCode)
    dispatch(
      notificationsActions.generalPageInfo({
        title: 'Copied!',
        msg: 'Successfully coped to clipboard',
      })
    )
    SegmentService.track('openInvite-link-copy', {
      worksiteName: worksite.alias,
    })
  } else {
    setSelectedWorksite(worksite)
    setIsInviteModalOpen(true)
  }
}

const getTableRows = (
  allWorksites: Worksite[],
  onRowClick: (Worksite) => void,
  menuCallback: (item: SelectItemProps) => void,
  dispatch: any,
  setSelectedWorksite: (worksite: Worksite) => void,
  setIsInviteModalOpen: (open: boolean) => void,
  isEditDisabled: boolean
) => {
  return allWorksites.map((worksite: Worksite, idx: number) => {
    const selectItems = !worksite.archived
      ? [
          {
            id: '0',
            text: 'Edit Worksite Info',
            worksite: worksite,
            disabled: isEditDisabled,
          },
          {
            id: '1',
            text: 'Invite Commuters',
            worksite: worksite,
            disabled: false,
          },
          {
            id: '2',
            text: 'Disable Invite Code',
            worksite: worksite,
            disabled: !worksite.inviteCode && true,
          },
          {
            id: '3',
            text: 'Archive Worksite',
            worksite,
            disabled:
              worksite.finchWorksite !== null ||
              Boolean(worksite.hasFinchEmployees),
          },
        ]
      : [
          {
            id: '4',
            text: 'Unarchive Worksite',
            worksite,
            disabled: false,
          },
        ]
    return (
      <TableRow key={idx} isClickable={!worksite.archived}>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          <WorksiteInfo>
            <Alias>{worksite.alias}</Alias>
            <WorksiteAddress>
              {worksite.formattedAddress.split(',')[0]}
            </WorksiteAddress>
          </WorksiteInfo>
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          <ColorPill
            color={worksite.archived ? 'gray' : 'green'}
            text={worksite.archived ? 'Archived' : 'Active'}
          />
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          <SegmentsDisplay
            segments={worksite?.programs?.map((program) => ({
              id: program,
              text: program,
              type: program,
            }))}
            displayType='table'
          />
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          <FlexContainer alignItems='center'>
            <LocationIcon />
            {worksite.city}
          </FlexContainer>
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          {worksite.currency ? (
            <PillContainer>{worksite.currency}</PillContainer>
          ) : (
            <Text variant='body2' textColor={palette.text.placeholder}>
              -
            </Text>
          )}
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(worksite)}>
          <FlexContainer alignItems='center'>
            <EmployeesIcon />
            {worksite.archived
              ? // This is a band-aid fix: when archiving a worksite with employees in staging, the archived employee count isn't getting updated
                // likely being caused by the get request happening before the bulk_archive_employees task is finished
                worksite.employeeArchivedCount + worksite.employeeCount
              : worksite.employeeCount}
          </FlexContainer>
        </TableRowCell>
        <TableRowCell style={{ width: '100px' }}>
          {worksite.archived ? (
            <> {moment(worksite.archiveDate).format('MMMM Do, YYYY')} </>
          ) : (
            <>
              <InviteContainer
                onClick={(e) =>
                  handleLinkClick(
                    e,
                    dispatch,
                    worksite,
                    setSelectedWorksite,
                    setIsInviteModalOpen
                  )
                }
              >
                <IconContainer>
                  <LinkIcon color={palette.text.secondary} />
                </IconContainer>
                <Text
                  variant='action4'
                  textColor={palette.text.secondary}
                  marginLeft='0.375rem'
                  hideOverflow
                  data-cy='accounts-worksite-table-invite-code'
                >
                  {worksite.inviteCode ? 'Copy Link' : 'Setup Invite Code'}
                </Text>
              </InviteContainer>
            </>
          )}
        </TableRowCell>
        <TableRowCell>
          <DropdownV2
            variant={'textual'}
            svgDropdown={true}
            usePortal={true}
            icon={<OverflowIcon />}
            active={null}
            items={selectItems}
            itemCallback={menuCallback}
            width={220}
            data-cy='accounts-worksite-table-dropdown'
          />
        </TableRowCell>
      </TableRow>
    )
  })
}

interface WorksiteViewProps {
  activeTab: number
  setActiveTab: (tab: number) => void
  tabs: { label: string; value: number }[]
}

const WorksiteView = React.memo((props: WorksiteViewProps) => {
  const { activeTab, setActiveTab, tabs } = props
  const allWorksites = useSelector(
    (state: AppState) => state.employer.worksites
  )
  const hrisStatus = useSelector((state: AppState) => ({
    providerName: state.employer.profile?.payrollProviderId,
    reauth: state.employer.profile?.finchReauth,
  }))

  const allFlexPrograms = useSelector(
    (state: AppState) => state.flex.flexPrograms
  )
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )

  const getWorksitesWithPrograms = () => {
    return allWorksites.map((worksite) => {
      return {
        ...worksite,
        programs: getAllProgramForColumn(
          allFlexPrograms,
          allGrhPrograms,
          allQtfPrograms,
          allMicromobilityPrograms,
          worksite.segmentId
        ),
      }
    })
  }

  const [workSitesToRender, setWorkSitesToRender] = useState(
    getWorksitesWithPrograms()
  )

  useEffect(() => {
    setWorkSitesToRender(getWorksitesWithPrograms())
  }, [allWorksites])

  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  const [selectedWorksite, setSelectedWorksite] = React.useState<Worksite>(null)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [isArchiveWorksiteModalOpen, setIsArchiveWorksiteModalOpen] =
    useState(false)
  const [isUnarchiveWorksiteModalOpen, setIsUnarchiveWorksiteModalOpen] =
    useState(false)
  const [isInviteCommutersModalOpen, setIsInviteCommutersModalOpen] =
    useState(false)
  const [isWorksiteInfoDrawerOpen, setIsWorksiteInfoDrawerOpen] =
    useState(false)

  const closeModal = useCallback(() => {
    setModalOpen(false)
    SegmentService.track('table-item-click', {
      tableType: 'worksites',
      action: 'close',
    })
  }, [])

  const menuCallback = (item: SelectItemProps) => {
    setSelectedWorksite(item.worksite)
    if (item.id === '0') {
      setModalOpen(true)
      SegmentService.track('table-item-click', {
        tableType: 'worksites',
        action: 'open',
        worksiteName: item.worksite && item.worksite.alias,
      })
    } else if (item.id === '1' && !item.disabled) {
      setIsInviteCommutersModalOpen(true)
    } else if (item.id === '2' && !item.disabled) {
      setIsInviteModalOpen(true)
    } else if (item.id === '3') {
      setIsArchiveWorksiteModalOpen(true)
    } else if (item.id === '4') {
      setIsUnarchiveWorksiteModalOpen(true)
    }
  }

  const [worksiteModalOpen, setWorksiteModalOpen] = useState(false)

  const openAddWorksiteModal = () => {
    setWorksiteModalOpen(true)
    SegmentService.track('addWorksite-modal-click', {
      action: 'open',
    })
  }

  const closeAddWorksiteModal = () => {
    setWorksiteModalOpen(false)
    SegmentService.track('addWorksite-modal-click', {
      action: 'close',
    })
  }

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Worksite'),
    HeaderCell('Status'),
    HeaderCell('Programs'),
    HeaderCell('City'),
    HeaderCell('Currency'),
    HeaderCell('Commuters'),
    HeaderCell('Signed Up Commuters'),
    HeaderCell('Edit'),
  ]

  const getCityFilters = () => {
    const object = {
      header: 'City',
      checkboxes: [],
    }
    getWorksitesWithPrograms().forEach((worksite) => {
      if (
        !object.checkboxes.find((checkbox) => checkbox.label === worksite.city)
      ) {
        object.checkboxes.push({
          label: worksite.city,
          checked: false,
          key: 'city',
          value: worksite.city,
        })
      }
    })
    return object
  }

  const filters = [
    {
      header: 'Status',
      checkboxes: [
        { label: 'Active', checked: false, key: 'archived', value: false },
        { label: 'Archived', checked: false, key: 'archived', value: true },
      ],
    },
    {
      header: 'Programs',
      checkboxes: [
        {
          label: wordings.flex.title,
          checked: false,
          key: 'programs',
          value: wordings.flex.title,
        },
        {
          label: wordings.grh.title,
          checked: false,
          key: 'programs',
          value: wordings.grh.title,
        },
        {
          label: wordings.qtf.title,
          checked: false,
          key: 'programs',
          value: wordings.qtf.title,
        },
        {
          label: wordings.micromobility.title,
          checked: false,
          key: 'programs',
          value: wordings.micromobility.title,
        },
      ],
    },
    getCityFilters(),
  ]

  const onRowClick = (worksite: Worksite) => {
    if (worksite.archived) {
      return
    }
    setSelectedWorksite(worksite)
    setIsWorksiteInfoDrawerOpen(true)
  }

  return (
    <>
      <PageHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        newGroupWorksiteButtonText='Create New Worksite'
        showCreateNewGroupButton
        createNewGroupWorksite={openAddWorksiteModal}
        isCreateNewGroupWorksiteDisabled={
          hrisStatus?.providerName ? true : false
        }
        newGroupWorksiteButtonTooltip={
          hrisStatus?.providerName
            ? 'You cannot create a new worksite while connected to a payroll provider through HRIS'
            : ''
        }
      />
      <PageBodyContainer>
        <TopContainer>
          <SegmentOperations
            filters={filters}
            searchArray={['alias', 'city', 'address']}
            segments={getWorksitesWithPrograms()}
            setFilteredSegments={setWorkSitesToRender}
            sortKey='alias'
          />
        </TopContainer>
        <Table
          headerCells={headerCells}
          rows={getTableRows(
            workSitesToRender,
            onRowClick,
            menuCallback,
            dispatch,
            setSelectedWorksite,
            setIsInviteModalOpen,
            hrisStatus?.providerName ? true : false
          )}
          stickyColumns={1}
        />
        {selectedWorksite && (
          <>
            <EditWorksiteModal
              worksite={selectedWorksite}
              open={modalOpen}
              closeModal={closeModal}
            />
            <ToggleWorksiteInviteCodeModal
              worksite={selectedWorksite}
              open={isInviteModalOpen}
              closeModal={() => setIsInviteModalOpen(false)}
            />
            <ArchiveWorksiteModal
              worksite={selectedWorksite}
              open={isArchiveWorksiteModalOpen}
              closeModal={() => setIsArchiveWorksiteModalOpen(false)}
            />
            <UnarchiveWorksiteModal
              worksite={selectedWorksite}
              open={isUnarchiveWorksiteModalOpen}
              closeModal={() => setIsUnarchiveWorksiteModalOpen(false)}
            />
            <InviteUsersModal
              segmentId={selectedWorksite.segmentId}
              closeModal={() => setIsInviteCommutersModalOpen(false)}
              open={isInviteCommutersModalOpen}
            />
            <SegmentInfoDrawer
              type='worksite'
              open={isWorksiteInfoDrawerOpen}
              closeModal={() => setIsWorksiteInfoDrawerOpen(false)}
              worksite={selectedWorksite}
            />
          </>
        )}
      </PageBodyContainer>
      <AddWorksiteModal
        open={worksiteModalOpen}
        closeModal={closeAddWorksiteModal}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  WorksiteView.displayName = 'WorksiteView'
}

export default WorksiteView
